import React, { useState, useContext, useEffect, useRef } from 'react';
import useApi from '../../../utils/api-manager/Helper/useApi';
import AuthContext from '../../../utils/secure-route/AuthContext';
import { useToastHandler } from '../../../components/common/ToastHandler';
import ReusableModal from '../../../components/Pages/AppPAges/ManageSettings/ReusableModal';
import ReusableTable from '../../common/ReusableComponent/reusableTable/ReusableTable';
import useSwal from '../../../components/common/ReusableComponent/useSwal';
import { ToastContainer } from 'react-toastify';
import useMyPermissions from '../../../utils/permissions/useMyPermissions';
import Can from '../../../utils/permissions/Can';

const WS_BASE = 'ws://127.0.0.1:8000';
const TABS = ['Alert Rules', 'Alert Logs'];

const OBJECT_TYPE_CHOICES = [
  ["person", "Person"],
  ["cash", "Cash"],
  ["hand", "Hand"],
  ["drawer", "Drawer"],
  ["phone", "Phone"],
  ["any", "Any Object"],
];

function AlertRule() {
  const { user } = useContext(AuthContext);
  const { can } = useMyPermissions();
  const { Post, Put, Delete, Get, Patch, GetPaginatedData, getAPI } = useApi();
  const { handleToast } = useToastHandler();
  const { confirmDelete } = useSwal();

  const [activeTab, setActiveTab] = useState('Alert Rules');
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [objectType, setObjectType] = useState('any');
  const [minConfidence, setMinConfidence] = useState(0.5);
  const [isActive, setIsActive] = useState(true);
  const [notifyWebsocket, setNotifyWebsocket] = useState(true);

  const [unreadOnly, setUnreadOnly] = useState(false);
  const [logsRefresh, setLogsRefresh] = useState(0);

  const [wsStatus, setWsStatus] = useState('');
  const [wsAlerts, setWsAlerts] = useState([]);
  const wsRef = useRef(null);

  useEffect(() => {
    if (!user?.store) return;
    const ws = new WebSocket(`${WS_BASE}/ws/moniter/${user.store}/`);
    wsRef.current = ws;
    ws.onopen = () => setWsStatus('connected');
    ws.onclose = () => setWsStatus('disconnected');
    ws.onerror = () => setWsStatus('error');
    ws.onmessage = (e) => {
      try {
        const data = JSON.parse(e.data);
        setWsAlerts(prev => [{ ...data, _ts: Date.now() }, ...prev].slice(0, 100));
        handleToast({ type: 'INFO', message: `Alert: ${data.object_type || 'Detection'} detected` });
      } catch { }
    };
    return () => ws.close();
  }, [user?.store]);

  useEffect(() => { setLogsRefresh(r => r + 1); }, [unreadOnly]);

  const fetchLogs = async ({ page = 1, searchTerm = '' } = {}) => {
    try {
      const params = new URLSearchParams();
      if (unreadOnly) params.append('unread', 'true');
      if (page > 1) params.append('page', page);
      if (searchTerm) params.append('search', searchTerm);
      const res = await Get(`${getAPI('alertLogData')}?${params.toString()}`);
      const results = res?.results ?? (Array.isArray(res) ? res : []);
      const count = res?.count ?? results.length;
      return { data: results, totalPages: Math.ceil(count / 10) };
    } catch { return { data: [], totalPages: 1 }; }
  };

  const markSingleRead = async (id) => {
    try {
      await Patch(`${getAPI('alertLogData')}${id}/mark-read`, '', {});
      setLogsRefresh(r => r + 1);
    } catch { handleToast({ type: 'ERROR', message: 'Failed to mark as read.' }); }
  };

  const markAllRead = async () => {
    try {
      await Patch(`${getAPI('alertLogData')}mark-all-read`, '', {});
      setLogsRefresh(r => r + 1);
      handleToast({ type: 'SUCCESS', message: 'All alerts marked as read.' });
    } catch { handleToast({ type: 'ERROR', message: 'Failed to mark all as read.' }); }
  };

  const triggerRefresh = () => setRefreshTrigger(p => p + 1);

  const fetchRules = async ({ page = 1, searchTerm = '' } = {}) => {
    try {
      setLoading(true);
      const response = await GetPaginatedData('alertRuleData', { page, search: searchTerm });
      const data = response?.data || [];
      return { data, totalPages: Math.ceil((response?.totalCount || data.length) / 10) };
    } catch { return { data: [], totalPages: 1 }; }
    finally { setLoading(false); }
  };

  const resetForm = () => { setObjectType('any'); setMinConfidence(0.5); setIsActive(true); setNotifyWebsocket(true); };

  const handleShow = (idOrItem = null, maybeItem = null) => {
    const item = maybeItem || (typeof idOrItem === 'object' ? idOrItem : null);
    if (item) {
      setEditing(item);
      setObjectType(item.object_type);
      setMinConfidence(item.min_confidence);
      setIsActive(item.is_active);
      setNotifyWebsocket(item.notify_websocket);
    } else { setEditing(null); resetForm(); }
    setShowModal(true);
  };

  const handleClose = () => { setShowModal(false); resetForm(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = { store: user.store, object_type: objectType, min_confidence: parseFloat(minConfidence), is_active: isActive, notify_websocket: notifyWebsocket };
    try {
      if (editing) { await Put('alertRuleData', editing.id, payload); handleToast({ type: 'SUCCESS', message: 'Alert rule updated!' }); }
      else { await Post('alertRuleData', payload); handleToast({ type: 'SUCCESS', message: 'Alert rule created!' }); }
      handleClose(); triggerRefresh();
    } catch { handleToast({ type: 'ERROR', message: 'Error saving alert rule.' }); }
  };

  const handleDelete = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;
    try { await Delete('alertRuleData', id); handleToast({ type: 'SUCCESS', message: 'Alert rule deleted!' }); triggerRefresh(); }
    catch { handleToast({ type: 'ERROR', message: 'Error deleting alert rule.' }); }
  };

  const ruleColumns = [
    { header: 'Object Type', field: 'object_type' },
    { header: 'Min Confidence', field: 'min_confidence' },
    { header: 'Active', field: 'is_active', render: (row) => row.is_active ? 'Yes' : 'No' },
    { header: 'Notify WebSocket', field: 'notify_websocket', render: (row) => row.notify_websocket ? 'Yes' : 'No' },
  ];

  const logColumns = [
    { header: 'Camera', field: 'camera_name' },
    { header: 'Message', field: 'message' },
    {
      header: 'Status', field: 'is_read',
      render: (row) => (
        <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 10px', borderRadius: '20px', background: row.is_read ? '#f0f0f0' : '#fff3dc', color: row.is_read ? '#888' : '#b8860b' }}>
          {row.is_read ? 'Read' : 'Unread'}
        </span>
      ),
    },
    {
      header: 'Fired At', field: 'fired_at',
      render: (row) => row.fired_at ? new Date(row.fired_at).toLocaleString() : '—',
    },
    // {
    //   header: 'Action', field: 'action',
    //   render: (row) => !row.is_read ? (
    //     <button onClick={() => markSingleRead(row.id)} style={{ background: '#fff8ec', border: '1px solid #ffe0a0', color: '#b8860b', borderRadius: '8px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '700' }}>
    //       Mark Read
    //     </button>
    //   ) : null,
    // },
  ];

  return (
    <>
      <style>{`
        @keyframes alertPulse { 0%,100%{opacity:1} 50%{opacity:0.3} }
        @keyframes fadeUp { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      <div className="responsive-box">
        <div className="header-row" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div className="setting-title" style={{ margin: 0 }}>Alert Rules</div>
          </div>
          {activeTab === 'Alert Rules' && (
            <Can module="mointerService" action="add">
              <button onClick={() => handleShow()} style={{ backgroundColor: '#FFAF32', border: 'none', borderRadius: '10px', padding: '8px 16px', fontWeight: 'bold', cursor: 'pointer', height: '42px' }}>
                + Add Alert Rule
              </button>
            </Can>
          )}
          {activeTab === 'Alert Logs' && (
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => setLogsRefresh(r => r + 1)} style={{ backgroundColor: '#f4f4f4', border: '1px solid #ddd', borderRadius: '10px', padding: '8px 14px', fontWeight: 'bold', cursor: 'pointer', height: '42px', fontSize: '16px' }} title="Refresh Logs">
                ↻
              </button>
              <button onClick={markAllRead} style={{ backgroundColor: '#282e26', color: '#F4FFEE', border: 'none', borderRadius: '10px', padding: '8px 16px', fontWeight: 'bold', cursor: 'pointer', height: '42px' }}>
                ✓ Mark All Read
              </button>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', background: '#f4f4f4', borderRadius: '50px', padding: '4px', gap: '3px', width: 'fit-content', marginBottom: '24px' }}>
          {TABS.map(t => (
            <button key={t} onClick={() => setActiveTab(t)} style={{ padding: '8px 22px', borderRadius: '50px', border: 'none', fontWeight: '700', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s', background: activeTab === t ? '#FFAF32' : 'transparent', color: activeTab === t ? '#1a1a2e' : '#777', boxShadow: activeTab === t ? '0 2px 8px rgba(255,175,50,0.3)' : 'none' }}>
              {t === 'Alert Logs' ? '🔔 Alert Logs' : '⚙️ Alert Rules'}
            </button>
          ))}
        </div>
        {activeTab === 'Alert Rules' && (
          <>
            <ReusableModal show={showModal} handleClose={handleClose} title={editing ? 'Edit Alert Rule' : 'Add Alert Rule'} width="520px">
              <form onSubmit={handleSubmit}>
                <div className="d-flex flex-column mt-4" style={{ gap: '16px' }}>
                  <div className="form-group product-field">
                    <select className="form-control-sales" value={objectType} onChange={e => setObjectType(e.target.value)} required>
                      {OBJECT_TYPE_CHOICES.map(([value, label]) => (
                        <option key={value} value={value}>{label}</option>
                      ))}
                    </select>
                    <label className="floating-label">Object Type</label>
                  </div>
                  <div className="form-group product-field">
                    <input type="number" className="form-control-sales" value={minConfidence} min="0" max="1" step="0.01" onChange={e => setMinConfidence(e.target.value)} required />
                    <label className="floating-label">Min Confidence (0–1)</label>
                  </div>
                  <div style={{ display: 'flex', gap: '24px', alignItems: 'center' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={isActive} onChange={e => setIsActive(e.target.checked)} /> Active
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                      <input type="checkbox" checked={notifyWebsocket} onChange={e => setNotifyWebsocket(e.target.checked)} /> Notify WebSocket
                    </label>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button type="submit" style={{ backgroundColor: '#282e26', color: '#F4FFEE', borderRadius: '40px', width: '160px', height: '38px', fontWeight: 'bold', border: 'none', cursor: 'pointer' }}>
                      {editing ? 'Update' : 'Save'}
                    </button>
                  </div>
                </div>
              </form>
            </ReusableModal>

            <ReusableTable
              fetchData={fetchRules} columns={ruleColumns} showAction={true}
              enablePagination={true} showFooter={true} onEdit={can("mointerService", "change") ? handleShow : undefined}
              onDelete={can("mointerService", "delete") ? handleDelete : undefined} loading={loading} refreshKey={refreshTrigger}
            />
          </>
        )}
        {activeTab === 'Alert Logs' && (
          <div style={{ animation: 'fadeUp 0.25s ease' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: '#444' }}>
                <div onClick={() => setUnreadOnly(v => !v)} style={{ width: '40px', height: '22px', borderRadius: '11px', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', background: unreadOnly ? '#FFAF32' : '#ddd' }}>
                  <div style={{ position: 'absolute', top: '3px', left: unreadOnly ? '21px' : '3px', width: '16px', height: '16px', borderRadius: '50%', background: '#fff', transition: 'left 0.2s', boxShadow: '0 1px 4px rgba(0,0,0,0.2)' }} />
                </div>
                Unread only
              </label>
            </div>
            <ReusableTable
              fetchData={fetchLogs}
              columns={logColumns}
              showAction={false}
              enablePagination={true}
              showFooter={true}
              refreshKey={logsRefresh}
            />
          </div>
        )}
      </div>
      {wsAlerts.length > 0 && (
        <div style={{ margin: '0 30px 20px', backgroundColor: '#fff', borderRadius: '14px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.07)', border: '1px solid #f0f0f0' }}>
          <div style={{ padding: '12px 18px', fontWeight: '700', fontSize: '13px', borderBottom: '1px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: '#1a1a2e', color: '#fff' }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#FFAF32', display: 'inline-block', animation: 'alertPulse 1.5s infinite' }} />
              Live Detections
            </span>
            <button onClick={() => setWsAlerts([])} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)', color: '#aaa', fontSize: '11px', borderRadius: '6px', padding: '3px 10px', cursor: 'pointer', fontWeight: '600' }}>
              Clear
            </button>
          </div>
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {wsAlerts.map((alert, i) => (
              <div key={i} style={{ padding: '10px 18px', borderBottom: '1px solid #fafafa', display: 'flex', gap: '12px', alignItems: 'center', fontSize: '13px' }}>
                <span style={{ fontSize: '15px' }}>⚡</span>
                <div style={{ flex: 1, display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {alert.object_type && <span style={{ background: '#fff8ec', color: '#b8860b', padding: '2px 9px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}>{alert.object_type}</span>}
                  {alert.confidence && <span style={{ color: '#666', fontSize: '12px' }}>Confidence: <b>{(alert.confidence * 100).toFixed(1)}%</b></span>}
                  {alert.camera && <span style={{ color: '#999', fontSize: '12px' }}>Camera: {alert.camera}</span>}
                </div>
                <span style={{ fontSize: '11px', color: '#bbb', flexShrink: 0 }}>{new Date(alert._ts).toLocaleTimeString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <ToastContainer />
    </>
  );
}

export default AlertRule;
